import {getApiBaseUrl} from '@/shared/api/config';
import {formatAmount, formatDetailNumeric, formatPercent, formatTradingPairDisplay, formatUTC, getActionLabel} from '@/shared/lib/format';

export const API_BASE_URL = getApiBaseUrl().replace(/\/$/, '');

/** 与 yesfi-exchange-explore/api/response.Result 一致 */
export interface ApiEnvelope<T> {
    code: number;
    /** 后端约定的错误描述字段。 */
    message?: string;
    /** 兼容旧版本可能返回 `msg`。 */
    msg?: string;
    data?: T;
}

export interface ExplorerPayloadDetail {
    symbol?: string;
    size?: string;
    size_delta?: string;
    price?: string;
    fill_price?: string;
    action?: string;
    position_side?: string;
    shares?: string | number;
    premium?: string;
    product_id?: string;
    touch_mode?: string;
    position_id?: string;
    payout_amount?: string;
    settlement_price?: string;
    settlement_price_ts?: string | number;
    tier?: string;
    target_price?: string;
    target_price1?: string;
    target_price2?: string;
    target_price3?: string;
    odds?: string;
    margin?: string;
    cell_key?: string;
    price_min?: string;
    price_max?: string;
    begin_time?: string | number;
    end_time?: string | number;
    ring?: string;
    ring_pct?: string;
    win_bands?: string;
    lower_price?: string;
    upper_price?: string;
    asset1?: string;
    asset2?: string;
    side?: string;
    handicap_pct?: string;
    handicap_tier?: string;
    target_handicap_pct?: string;
    symbol1?: string;
    symbol2?: string;
    symbol3?: string;
    side1?: string;
    side2?: string;
    side3?: string;
    amplitude_pct1?: string;
    amplitude_pct2?: string;
    amplitude_pct3?: string;
    selected_tier?: string;
    tier_lower_pct?: string;
    tier_upper_pct?: string;
    settlement_high_price?: string;
    settlement_low_price?: string;
    // 开仓时仓位总保证金 M_total = M_user + M_coupon（core openAmt.RawRM / lite-option split.TotalMargin）；
    // explore 开仓 amount 展示用，统一口径（不依赖选中分录金额正负）。
    total_margin?: string;
    // 平仓时券剩余回 COUPON_POOL 的量（回收量，core/SI 平仓 couponAlloc.CouponReturn）；explore PAYOUT YFV Receive 展示。
    coupon_return?: string;
    // 券使用量（core/SI/funding/期权开仓/期权结算 顶层；insurance 在 policies[] 内）
    coupon_id?: string;
    coupon_amount?: string;
    coupon_used_margin?: string;
    coupon_deducted_amount?: string;
    // 保险 detail：per-policy 字段（insurance 服务）
    policies?: ExplorerInsurancePolicyDetail[];
}

/** 保险 detail 的单张保单字段（insurance 服务 per-policy；含券抵扣保费）。 */
export interface ExplorerInsurancePolicyDetail {
    insurance_type?: string;
    duration_hours?: number;
    price?: string;
    payout_amount?: string;
    trigger_price?: string;
    coupon_id?: string;
    coupon_amount?: string;
    coupon_deducted_amount?: string;
    user_premium?: string;
}

/**
 * `payload_json` 解析后与 yesfi-exchange-explore `BuildPayloadJSON` 对齐：
 * 新数据含 `amount`、`asset`；开仓/关仓/清算等可能含 `detail`（含 `symbol` 等）。
 */
export interface ExplorerPayloadJSON {
    amount?: string;
    asset?: string;
    detail?: ExplorerPayloadDetail;
    /** @deprecated 已不在新写入中出现 */
    source?: string;
    /** @deprecated 已由表列 tx_id 取代 */
    tx_id?: string;
}

/** 列表 Asset 列：账本金额统一以 USDC 计价。 */
export const TX_LIST_DISPLAY_ASSET = 'USDC';

/** explorer_tx_record JSON（database/gdb/table.ExplorerTxRecord） */
export interface ExplorerTxRecord {
    id: number;
    tx_hash: string;
    wallet_address: string;
    to_address?: string;
    biz_type: string;
    asset: string;
    amount: string;
    block_height: number;
    /** 毫秒时间戳（与链上 block 对齐规则一致） */
    block_time: number;
    /** JSON 字符串，形态见 {@link ExplorerPayloadJSON} */
    payload_json: string;
    created_at_ms: number;
}

/** 解析 `payload_json`；非 object / 非法 JSON 一律返回 `null`。 */
export const parseExplorerPayloadJSON = (raw: string | null | undefined): ExplorerPayloadJSON | null => {
    const s = (raw ?? '').trim();
    if (!s) return null;
    try {
        const v: unknown = JSON.parse(s);
        if (v && typeof v === 'object' && !Array.isArray(v)) {
            return v as ExplorerPayloadJSON;
        }
        return null;
    } catch {
        return null;
    }
};

const mapTrendSideDisplay = (side: string | null | undefined): string | null => {
    const raw = side?.trim();
    if (!raw) return null;
    switch (raw.toLowerCase()) {
        case 'up':
            return 'UP';
        case 'down':
            return 'DOWN';
        default:
            return raw.toUpperCase();
    }
};

/** Action 列展示：与接口 `biz_type` 一致（下划线转空格、大写）。 */
export const getTxDisplayAction = (tx: ExplorerTxRecord): string => getActionLabel(tx.biz_type);

/** Amount 展示：用户侧 balance 变化（payload_json 同层级的 amount，带符号），统一追加 USDC。 */
export const getTxDisplayAmount = (tx: ExplorerTxRecord, maxDecimals = 8): string =>
    formatAmount(tx.amount, 'USDC', maxDecimals);

/** 详情页 Symbol：来自 detail.symbol / asset / symbol1-3，并做交易对格式化。 */
const firstDetailValue = (...values: Array<string | number | null | undefined>): string | null => {
    for (const value of values) {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return String(value);
        }
        const trimmed = typeof value === 'string' ? value.trim() : '';
        if (trimmed) return trimmed;
    }
    return null;
};

const mapTouchModeDisplay = (touchMode: string | null | undefined): string | null => {
    const raw = touchMode?.trim();
    if (!raw) return null;
    switch (raw.toLowerCase()) {
        case 'long':
            return 'LONG';
        case 'short':
            return 'SHORT';
        default:
            return raw.toUpperCase();
    }
};

const formatDetailTimestamp = (value: string | number | null | undefined): string | null => {
    if (value == null) return null;
    const ms = typeof value === 'number' ? value : Number(String(value).trim());
    if (!Number.isFinite(ms)) return null;
    const formatted = formatUTC(ms);
    return formatted === '—' ? null : formatted;
};

export interface TxDetailField {
    label: string;
    value: string | null;
}

// 开仓 biz_type（YFV Payment = 券使用量 coupon_used_margin）
const OPEN_BIZ_TYPES = new Set([
    'POSITION_OPEN', 'OPEN_POSITION', 'OPTION_OPEN',
    'TREND_OPEN', 'RANGE_OPEN', 'STEPS_OPEN', 'COMBO_OPEN', 'PAIR_OPEN', 'MOVES_OPEN', 'BEATS_OPEN',
]);
// 平仓/结算 biz_type（YFV Receive = 券回收量 coupon_return）
const PAYOUT_BIZ_TYPES = new Set([
    'POSITION_CLOSE', 'CLOSE_POSITION', 'LIQUIDATION',
    'OPTION_PAYOUT', 'TREND_PAYOUT', 'RANGE_PAYOUT', 'STEPS_PAYOUT', 'COMBO_PAYOUT', 'PAIR_PAYOUT', 'MOVES_PAYOUT', 'BEATS_PAYOUT',
]);
// Extra Data 跳过的 detail 顶层 key（YFV 单独展示或不展示；action 与 Action 列重复）
const DETAIL_SKIP_KEYS = new Set([
    'coupon_used_margin', 'coupon_deducted_amount', 'total_margin', 'coupon_amount', 'coupon_return', 'policies', 'action', 'user_margin',
]);
// 是否应跳过的 key（含 _id 后缀的业务 ID，如 position_id / product_id / coupon_id）。
const isDetailSkipKey = (key: string): boolean => DETAIL_SKIP_KEYS.has(key) || key.endsWith('_id');
// detail key → 友好 label 映射（Extra Data 展示用）
const DETAIL_LABELS: Record<string, string> = {
    symbol: 'Symbol', size: 'Size', price: 'Price', fill_price: 'Fill Price', position_side: 'Position Side', side: 'Side',
    shares: 'Shares', premium: 'Premium', product_id: 'Product ID', touch_mode: 'Touch Mode', position_id: 'Position ID',
    payout_amount: 'Payout Amount', settlement_price: 'Settlement Price', settlement_price_ts: 'Settlement Time',
    settlement_high_price: 'Settlement High', settlement_low_price: 'Settlement Low', tier: 'Tier',
    target_price: 'Target Price', target_price1: 'Target Price 1', target_price2: 'Target Price 2', target_price3: 'Target Price 3',
    odds: 'Exercise', margin: 'Margin', cell_key: 'Cell Key', price_min: 'Price Min', price_max: 'Price Max',
    begin_time: 'Begin Time', end_time: 'End Time', ring: 'Ring', ring_pct: 'Ring Pct', win_bands: 'Win Bands',
    lower_price: 'Lower Price', upper_price: 'Upper Price', asset1: 'Asset 1', asset2: 'Asset 2',
    handicap_pct: 'Handicap Pct', handicap_tier: 'Handicap Tier', target_handicap_pct: 'Target Handicap Pct',
    symbol1: 'Symbol 1', symbol2: 'Symbol 2', symbol3: 'Symbol 3',
    side1: 'Side 1', side2: 'Side 2', side3: 'Side 3',
    amplitude_pct1: 'Amplitude Pct 1', amplitude_pct2: 'Amplitude Pct 2', amplitude_pct3: 'Amplitude Pct 3',
    selected_tier: 'Selected Tier', tier_lower_pct: 'Tier Lower Pct', tier_upper_pct: 'Tier Upper Pct',
    user_margin: 'User Margin', total_margin: 'Total Margin',
};
const DETAIL_TIMESTAMP_KEYS = new Set(['settlement_price_ts', 'begin_time', 'end_time']);
const DETAIL_PERCENT_KEYS = new Set(['ring_pct', 'handicap_pct', 'tier_lower_pct', 'tier_upper_pct', 'amplitude_pct1', 'amplitude_pct2', 'amplitude_pct3', 'target_handicap_pct']);
const DETAIL_NUMERIC_KEYS = new Set(['size', 'price', 'fill_price', 'settlement_price', 'settlement_high_price', 'settlement_low_price', 'target_price', 'target_price1', 'target_price2', 'target_price3', 'lower_price', 'upper_price', 'price_min', 'price_max', 'payout_amount', 'premium', 'trigger_price', 'shares', 'odds']);
const DETAIL_SIDE_KEYS = new Set(['side', 'position_side', 'side1', 'side2', 'side3']);

/** formatDetailField 按 key 友好展示 detail 字段值。 */
const formatDetailField = (key: string, raw: unknown): string | null => {
    if (raw == null || String(raw).trim() === '') return null;
    if (key === 'symbol') return formatTradingPairDisplay(String(raw));
    if (DETAIL_TIMESTAMP_KEYS.has(key)) return formatDetailTimestamp(raw as string | number);
    if (key === 'odds') return formatOdds(String(raw));
    if (DETAIL_PERCENT_KEYS.has(key)) return formatPercent(String(raw));
    if (DETAIL_NUMERIC_KEYS.has(key)) return formatDetailNumeric(String(raw));
    if (DETAIL_SIDE_KEYS.has(key)) return mapTrendSideDisplay(raw as string);
    if (key === 'touch_mode') return mapTouchModeDisplay(raw as string);
    return firstDetailValue(raw as string | number);
};

const formatOdds = (value: string | null | undefined): string | null => {
    const v = (value ?? '').trim();
    if (!v) return null;
    return `${parseFloat(v)} x`;
};

/** 交易详情页 Extra Data：detail 字段每个属性一行（友好展示）+ YFV Payment/Receive + 保险 policies。 */
export const getTxDetailFields = (tx: ExplorerTxRecord): TxDetailField[] => {
    const detail = parseExplorerPayloadJSON(tx.payload_json)?.detail;
    if (!detail) return [];
    const bizType = (tx.biz_type ?? '').trim().toUpperCase();
    const isCombo = bizType === 'COMBO_OPEN';
    const isPair = bizType === 'PAIR_OPEN';
    const fields: TxDetailField[] = [];
    // Extra Data：detail 顶层字段，每个属性一行（友好展示）；跳过 YFV/不展示/PAIR side/COMBO 合并字段。
    for (const [key, raw] of Object.entries(detail)) {
        if (isDetailSkipKey(key)) continue;
        if (isPair && key === 'side') continue; // PAIR side 是选择器（asset1/asset2），asset1/asset2 行已展示标的
        if (isCombo && (key === 'side1' || key === 'side2' || key === 'side3' || key === 'target_price1' || key === 'target_price2' || key === 'target_price3')) continue; // COMBO 组合展示
        const value = formatDetailField(key, raw);
        if (value == null || value === '—') continue;
        const label = DETAIL_LABELS[key] ?? key;
        fields.push({ label, value });
    }
    // COMBO 组合展示：side1/2/3 合并 Side，target_price1/2/3 合并 Target Price。
    if (isCombo) {
        const comboSides = [detail.side1, detail.side2, detail.side3].map(s => mapTrendSideDisplay(s)).filter((v): v is string => !!v);
        if (comboSides.length) fields.push({ label: 'Side', value: comboSides.join(' / ') });
        const comboTargets = [detail.target_price1 ?? detail.target_price, detail.target_price2, detail.target_price3].map(p => formatDetailNumeric(p)).filter((v): v is string => !!v);
        if (comboTargets.length) fields.push({ label: 'Target Price', value: comboTargets.join(' / ') });
    }
    // 保险 policies[0] 字段（一行一个）。
    const policy = detail.policies?.[0];
    if (policy) {
        if (policy.insurance_type) fields.push({ label: 'Insurance Type', value: firstDetailValue(policy.insurance_type) });
        if (policy.duration_hours != null) fields.push({ label: 'Duration Hours', value: String(policy.duration_hours) });
        if (policy.price) fields.push({ label: 'Premium', value: formatDetailNumeric(policy.price) });
        if (policy.payout_amount) fields.push({ label: 'Payout Amount', value: formatDetailNumeric(policy.payout_amount) });
        if (policy.trigger_price) fields.push({ label: 'Trigger Price', value: formatDetailNumeric(policy.trigger_price) });
    }
    return fields;
};

/** YFV Payment / YFV Receive：从 detail 中提取券相关金额，独立于 Extra Data 展示。 */
export const getTxYfvFields = (tx: ExplorerTxRecord): TxDetailField[] => {
    const result: TxDetailField[] = [];
    const detail = parseExplorerPayloadJSON(tx.payload_json)?.detail;
    if (!detail) return result;
    const bizType = tx.biz_type ?? '';
    const isOpen = OPEN_BIZ_TYPES.has(bizType);
    const isFunding = bizType === 'FUNDING_FEE';
    const isPayout = PAYOUT_BIZ_TYPES.has(bizType);
    if ((isOpen || isFunding) && detail.coupon_amount) {
        result.push({ label: 'YFV Payment', value: formatAmount(String(detail.coupon_amount), 'USDC', 8) });
    }
    // YFV Receive = core 平仓真实回收到 COUPON_POOL 的量（detail.coupon_return，core closeCouponReturnStr）。
    // 部分平仓亏损场景 coupon_return 为空/0（券被亏损吃光，无回收）不展示；全平/盈利场景展示真实回收量。
    // 旧实现误用 coupon_amount - coupon_deducted_amount - coupon_deducted_loss：core TradeDetail 从不输出
    // coupon_deducted_loss（恒 0），且未用 core 已正确提供的 coupon_return，导致部分平仓亏损场景
    // 错误展示为券面值（如 30 - 0 - 0 = 30），与真实回收量 0 不符。
    if (isPayout && detail.coupon_return) {
        const returned = parseFloat(String(detail.coupon_return));
        if (returned > 0) {
            result.push({ label: 'YFV Receive', value: formatAmount(String(returned), 'USDC', 8) });
        }
    }
    return result;
};

/** 交易详情页 Extra Data：以 JSON 格式逐行展示 detail 字段（不含 YFV Payment/Receive）。 */
export const getTxExtraDataJson = (tx: ExplorerTxRecord): TxDetailField => {
    const fields = getTxDetailFields(tx);
    const entries: string[] = [];
    for (const f of fields) {
        const label = f.label.replace(/ /g, '_').toLowerCase();
        const v = f.value ?? '';
        // 去掉常见后缀后再尝试数字解析（exercise 的 "x" 后缀等）
        const raw = v.replace(/,/g, '');
        const num = Number(raw);
        const val = !isNaN(num) && raw !== '' ? raw : `"${v}"`;
        entries.push(`  "${label}": ${val}`);
    }
    return { label: 'Extra Data', value: entries.length > 0 ? '{\n' + entries.join(',\n') + '\n}' : '' };
};

export interface PageResult<T> {
    total: number;
    pageNo: number;
    pageSize: number;
    list: T[];
}

/**
 * 地址账户快照：与后端 model.AccountSnapshot 对齐。
 * 后端从 yesfi-exchange-gateway POST /v1/public/explorer/account 的 data.account.{currency,equity}
 * 中裁剪后透传；equity 为字符串十进制以保留精度；下游不可用时整体为 null。
 */
export interface AddressAccountSnapshot {
    /** 计价币种（如 "USDC"） */
    currency: string;
    /** 账户权益（字符串十进制） */
    equity: string;
}

/** cursor 分页结果（与后端 model.AddressTxRecordCursorView 对齐）。 */
export interface CursorPageResult<T> {
    pageSize: number;
    cursor: number;
    nextCursor: number;
    hasMore: boolean;
    list: T[];
}

/** 地址 cursor 查询响应：列表 + 账户快照。 */
export interface AddressCursorPage extends CursorPageResult<ExplorerTxRecord> {
    account: AddressAccountSnapshot | null;
}
export type BlockPage = PageResult<ExplorerTxRecord>;

export const totalPages = (page: Pick<PageResult<unknown>, 'total' | 'pageSize'>): number => {
    const { total, pageSize } = page;
    if (pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(total / pageSize));
};

/** API 错误：携带 HTTP status、后端 envelope.code 与可读 message。 */
export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly code: number,
        message: string,
    ) {
        super(message);
        this.name = 'ApiError';
    }

    /** 资源不存在：HTTP 404 或后端约定的 NotFound code。 */
    get isNotFound(): boolean {
        return this.status === 404 || this.code === 4040 || this.code === 1404;
    }

    /** 用户主动取消，不应展示错误。 */
    get isAborted(): boolean {
        return false;
    }
}

/** 将原生 AbortError / TypeError 包装为统一类型，便于上层 `instanceof` 判断。 */
export const isAbortError = (err: unknown): boolean =>
    !!err && typeof err === 'object' && (err as { name?: string }).name === 'AbortError';

const readEnvelope = async <T>(response: Response): Promise<ApiEnvelope<T>> => {
    const text = await response.text();
    if (!text) {
        return { code: response.ok ? 0 : -1, message: response.ok ? '' : `HTTP ${response.status}` };
    }
    try {
        return JSON.parse(text) as ApiEnvelope<T>;
    } catch {
        throw new ApiError(response.status, -1, `HTTP ${response.status}: invalid JSON`);
    }
};

const handleResponse = async <T>(response: Response): Promise<T> => {
    const envelope = await readEnvelope<T>(response);
    const msg = envelope.message || envelope.msg || '';

    if (!response.ok) {
        throw new ApiError(response.status, envelope.code ?? -1, msg || `HTTP ${response.status}`);
    }
    if (envelope.code !== 0) {
        throw new ApiError(response.status, envelope.code, msg || `API error (code ${envelope.code})`);
    }
    if (envelope.data === undefined) {
        throw new ApiError(response.status, envelope.code, msg || 'API returned success without data');
    }
    return envelope.data;
};

/** 统一处理 fetch 失败：网络错误、Abort 都抛 `ApiError`，便于上层分类处理。 */
const safeFetch = async (url: string, init?: RequestInit): Promise<Response> => {
    try {
        return await fetch(url, init);
    } catch (err) {
        if (isAbortError(err)) {
            throw err;
        }
        throw new ApiError(0, -1, err instanceof Error ? err.message : 'Network error');
    }
};

export interface RequestOptions {
    signal?: AbortSignal;
}

export const api = {
    getAddressCursor: (
        address: string,
        cursor = 0,
        pageSize = 20,
        bizType?: string,
        opts: RequestOptions = {},
    ) => {
        const params = new URLSearchParams({
            cursor: String(cursor),
            pageSize: String(pageSize),
        });
        const trimmedBizType = bizType?.trim();
        if (trimmedBizType) params.set('bizType', trimmedBizType);
        return safeFetch(
            `${API_BASE_URL}/address-cursor/${encodeURIComponent(address)}?${params}`,
            { signal: opts.signal },
        ).then(res => handleResponse<AddressCursorPage>(res));
    },

    getTransaction: (txHash: string, opts: RequestOptions = {}) =>
        safeFetch(`${API_BASE_URL}/tx/${encodeURIComponent(txHash)}`, { signal: opts.signal }).then(res =>
            handleResponse<ExplorerTxRecord>(res),
        ),

    getBlock: (blockHeight: string | number, pageNo = 1, pageSize = 50, opts: RequestOptions = {}) =>
        safeFetch(
            `${API_BASE_URL}/block/${encodeURIComponent(String(blockHeight))}?pageNo=${pageNo}&pageSize=${pageSize}`,
            { signal: opts.signal },
        ).then(res => handleResponse<BlockPage>(res)),

    getLatestTransactions: (opts: RequestOptions = {}) =>
        safeFetch(`${API_BASE_URL}/last`, { signal: opts.signal }).then(res =>
            handleResponse<ExplorerTxRecord[]>(res),
        ),

    /** 与 /explorer/getTimestamp/{height} 对齐，毫秒时间戳 */
    getBlockTimestamp: (blockHeight: string | number, opts: RequestOptions = {}) =>
        safeFetch(`${API_BASE_URL}/getTimestamp/${encodeURIComponent(String(blockHeight))}`, {
            signal: opts.signal,
        }).then(res => handleResponse<number>(res)),
};
